import { useEffect, useState } from 'react';

import config from '@/config';
import { NextButton, PrevButton } from '@/shared-components/buttons/components/SliderControls';

import BoardArtworkPreview from './BoardArtworkPreview';

import '@/shared-components/forms/add-board-content/board-text-body/add-element/add-picture/BoardBodyPicture.css';

/**
 * ArtworkCarousel replicates InlineImageCarousel logic for artworks,
 * showing a slider of BoardArtworkPreview components with controls
 * placed below each caption.
 *
 * Props:
 *   artworks: Array<{ artworkId, imageUrl, caption, slug }>
 *   onRemoveArtwork: fn(artworkId)
 *   previewMode: boolean
 */
export default function ArtworkCarousel({
  groupId,
  artworks = [],
  onRemoveArtwork,
  previewMode = false,
  readOnly = false,
  initialSlideIndex = 0,
  carouselId, 
  navigate, // important to pass it here, it cannot be within the artwork carousel
  location, // important to pass it here, it cannot be within the artwork carousel
}) {
  // only grab slideIndex *and* carouselId from history.state
  const { slideIndex, carouselId: savedCarouselId } = location.state || {};
  // initialize activeSlide:
  //   • if this carouselId matches the saved one, use slideIndex
  //   • otherwise fall back to initialSlideIndex
  const [activeSlide, setActiveSlide] = useState(
    typeof slideIndex === 'number' && savedCarouselId === carouselId
      ? slideIndex
      : initialSlideIndex
  );
 
  // → when you “go back” with a slideIndex+carouselId,
  //    update _only_ that carousel to the remembered slide
  useEffect(() => {
    if (typeof slideIndex === 'number' && savedCarouselId === carouselId) {
      setActiveSlide(slideIndex);
    }
    if (typeof slideIndex === 'number' && savedCarouselId === carouselId) {
      setActiveSlide(slideIndex);
    }
  }, [slideIndex, savedCarouselId, carouselId]);

  const onClickSlide = idx => {
    navigate(`/artwork/${artworks[idx].slug}`, {
      state: {
        from: location.pathname,
        scrollY: window.scrollY,
        slideIndex: idx,
        carouselId,
        artworkViewSource: {
          type: 'community',
          label: '',
        },
      }
    });
  };

  // Preload all artwork images at max width
  useEffect(() => {
    artworks.forEach(item => {
      const img = new Image();
      img.src = `${config.app.imageServiceDomain}/${item.imageUrl}/w=1200`;
    });
  }, [artworks]);

  // Clamp activeSlide if artworks array length changes
  useEffect(() => {
    if (activeSlide >= artworks.length) {
      setActiveSlide(Math.max(0, artworks.length - 1));
    }
  }, [activeSlide, artworks.length]);

  if (!artworks || artworks.length === 0) {
    return null;
  }

  const safeIndex = Math.min(activeSlide, artworks.length - 1);
  const current = artworks[safeIndex] || null;

  if (!current) {
    // defensive: shouldn't happen, but guard before we read .imageUrl
    return null;
  }

  const removeSlide = (artworkId) => {
    setActiveSlide(currentSlide => {
      if(artworks.length === 1) return 0; // if last slide, reset to 0
      if(currentSlide === 0) return 0; // if first slide, stay at 0
      return currentSlide - 1;
    });
    onRemoveArtwork(groupId, artworkId);
  };

  // Slider controls (dots + prev/next)
  const sliderControls = artworks.length > 1 && (
    <div className="artwork-view-pictures-slider__control-indicators-container" contentEditable={false}>
      <div className="artwork-view-pictures-slider__control-outter prev">
        <PrevButton
          onClick={e => { e.stopPropagation(); setActiveSlide(i => Math.max(0, i - 1)); }}
          className={`artwork-view-pictures-slider__control ${safeIndex === 0 ? 'disabled' : ''}`}
        />
      </div>
      <div className="artwork-view-pictures-section__slide-indicators">
        {artworks.map((_, idx) => (
          <div key={idx} className={`artwork-view-pictures-section__slide-indicator${safeIndex === idx ? ' active' : ''}`} />
        ))}
      </div>
      <div className="artwork-view-pictures-slider__control-outter next">
        <NextButton
          onClick={e => { e.stopPropagation(); setActiveSlide(i => Math.min(artworks.length - 1, i + 1)); }}
          className={`artwork-view-pictures-slider__control ${safeIndex === artworks.length - 1 ? 'disabled' : ''}`}
        />
      </div>
    </div>
  );

  return (
    <div className="artwork-carousel">
      <BoardArtworkPreview
        image={current.imageUrl}
        caption={current.caption}
        slug={current.slug}
        onClick={() => onClickSlide(safeIndex)}
        /* removal still allowed during edit (previewMode=false) */
        onRemove={!previewMode ? () => removeSlide(current.artworkId) : undefined}
        sliderControls={sliderControls}
        /* now only respect the explicit readOnly flag (not previewMode) */
        readOnly={readOnly}
        slideIndex={safeIndex}
      />
    </div>
  );
}
